import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react'

export { default as Button } from './Button'
export { default as SelectMultiple } from './SelectMultiple'
export { default as SubmitButton } from './SubmitButton'

export * from './AutoComplete'
export * from './Checkbox'
export * from './CheckboxStandalone'
export * from './DatePicker'
export * from './DateTimePicker'
export * from './Dropzone'
export * from './ErrorMessage'
export * from './Input'
export * from './ModalSubmitButton'
export * from './Select'
export * from './SelectDashboard'
export * from './Textarea'
export * from './TimePicker'
export * from './TimeInput'
export * from './ToggleButton'

// @deprecated
export * from './TemporaryDateTimePicker'

export const FormContext = createContext()

export default function Form({ children, onUpdate, onSubmit }) {
  const [data, setData] = useState({})

  const updateItem = useCallback(
    item => {
      setData(data => {
        const newState = {
          ...data,
          [item.name]: { ...data[item.name], ...item },
        }
        onUpdate &&
          onUpdate(
            item,
            newState,
            item => setData({ ...newState, ...item }),
            item => deleteItem(item),
          )
        return newState
      })
    },
    [data, onUpdate],
  )

  const submit = useCallback(() => {
    const isValid = Object.entries(data)
      .map(([name, item]) => !item.runValidation())
      .every(item => item)
    onSubmit && onSubmit(data, isValid)
  }, [data, onSubmit])

  const deleteItem = useCallback(
    name => {
      setData(data => {
        const newData = { ...data }
        if (newData[name]) {
          delete newData[name]
        }
        return newData
      })
    },
    [data],
  )

  return (
    <FormContext.Provider value={{ updateItem, submit, deleteItem, data }}>
      {children}
    </FormContext.Provider>
  )
}

export function useFormChildLogic(props) {
  const { validator, name, initValue } = props
  const form = useContext(FormContext)
  const [value, setValue] = useState(initValue)
  const [error, setError] = useState('')

  const updateForm = useCallback(
    value => {
      setValue(value)
      if (form) {
        form.updateItem({ name, value })
        findError(value)
      }
    },
    [form, value],
  )

  const findError = useCallback(
    value => {
      if (validator) {
        let error
        form.updateItem({ name, error: '', value })
        for (let i = 0; i < validator.length; i++) {
          error = validator[i](value)
          if (error) {
            form.updateItem({ name, error, value })
            break
          }
        }
        setError(error)
        return error
      } else {
        setError('')
      }
    },
    [validator],
  )

  useEffect(() => {
    if (form) {
      if (value !== undefined) {
        form.updateItem({
          name,
          value,
          isDirty: true,
          isValid: !findError(value),
          runValidation: () => findError(value),
        })
      } else {
        form.updateItem({
          name,
          isDirty: false,
          isValid: false,
          runValidation: () => findError(value),
        })
      }
    }
  }, [value, validator])

  useEffect(() => () => form && form.deleteItem(name), [])

  return {
    updateForm,
    findError,
    value,
    error,
  }
}
